import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton } from '@hotjar/hotsauce';
import { sendHighlightEvent } from '@hotjar/webapp/domains/highlights/api';
import type { UIPosition } from '@hotjar/webapp/domains/highlights/api';
import { HighlightsEventType } from '@hotjar/webapp/domains/highlights/dto';
import { useCurrentSiteId } from '@hotjar/site';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';
import type { HeatmapHighlight } from '@hotjar/webapp/domains/highlights/model';

import type { HeatmapImageRef } from './HeatmapImage';
import { useTranslatedClickEvent } from './use-translated-click-event';
import { downloadHeatmapHighlight } from './download-helper';

const HEATMAP_HIGHLIGHT_DOWNLOADED = 'heatmap_highlight_downloaded';

interface DownloadButtonProps {
  className?: string;
  highlight: HeatmapHighlight;
  svgRef: React.RefObject<HeatmapImageRef>;
  uiPosition?: UIPosition;
}

export const DownloadButton = ({
  className,
  highlight,
  svgRef,
  uiPosition,
}: DownloadButtonProps): JSX.Element | null => {
  const { t } = useTranslation();
  const currentSiteId = useCurrentSiteId();

  const eventCopy = useTranslatedClickEvent({
    count: highlight.metadata.clicksCount ?? 0,
    isDesktop: highlight.metadata.deviceType === 'desktop',
  });

  const startDownload = async () => {
    if (currentSiteId) {
      sendHighlightEvent(
        currentSiteId,
        { highlightId: highlight.id, eventType: HighlightsEventType.HIGHLIGHT_DOWNLOADED },
        { uiPosition },
      );
    }

    event(HEATMAP_HIGHLIGHT_DOWNLOADED);

    if (!svgRef.current) {
      return;
    }

    const svg = svgRef.current.get();

    if (!svg) {
      return;
    }

    await downloadHeatmapHighlight({
      svg,
      highlight,
      eventCopy,
    });
  };

  return (
    <IconButton
      description={t('highlights:single.download')}
      className={className}
      icon="download"
      kind="ghost"
      onClick={startDownload}
      size="s"
      tooltipText={t('highlights:single.download')}
    />
  );
};
